import { useState } from "react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../LanguageSwitcher";

const Header = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMenuOpen(false);
    }
  };

  const menuItems = [
    { id: "hero", label: t("nav.home") },
    { id: "features", label: t("nav.features") },
    { id: "how-it-works", label: t("nav.howItWorks") },
    { id: "about", label: t("nav.about") },
    { id: "contact", label: t("nav.contact") },
  ];

  return (
    <header className="sticky top-0 z-50 bg-dark-950/95 backdrop-blur-2xl border-b border-neuro-cyan/30 shadow-lg shadow-neuro-cyan/10">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <nav className="flex justify-between items-center h-20">
          <div
            className="logo group cursor-pointer flex items-center gap-4 transform hover:scale-105 transition-all duration-300"
            onClick={() => scrollToSection("hero")}
          >
            <div
              className="hexagon-small bg-linear-to-br from-neuro-cyan/30 to-neuro-azure/20 neuro-glow-cyan group-hover:from-neuro-cyan/40 group-hover:to-neuro-azure/30 transition-all duration-500 animate-hex-glow relative"
              style={{ width: "48px", height: "55px" }}
            >
              <div className="absolute inset-0 flex items-center justify-center animate-pulse-glow">
                <svg
                  className="w-7 h-7 text-neuro-cyan drop-shadow-lg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="3"
                    strokeWidth="2"
                    fill="currentColor"
                    className="animate-pulse"
                  />
                  <path
                    d="M12 12 L18 6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M12 12 L6 6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M12 12 L18 18"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M12 12 L6 18"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M12 12 L12 3"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="18"
                    cy="6"
                    r="2"
                    strokeWidth="1.5"
                    fill="none"
                    className="opacity-70"
                  />
                  <circle
                    cx="6"
                    cy="6"
                    r="2"
                    strokeWidth="1.5"
                    fill="none"
                    className="opacity-70"
                  />
                  <circle
                    cx="18"
                    cy="18"
                    r="2"
                    strokeWidth="1.5"
                    fill="none"
                    className="opacity-70"
                  />
                  <circle
                    cx="6"
                    cy="18"
                    r="2"
                    strokeWidth="1.5"
                    fill="none"
                    className="opacity-70"
                  />
                  <circle
                    cx="12"
                    cy="3"
                    r="2"
                    strokeWidth="1.5"
                    fill="none"
                    className="opacity-70"
                  />
                </svg>
              </div>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold gradient-text group-hover:scale-105 transition-transform duration-300">
              Neuro Cell
            </h2>
          </div>

          <button
            className={`md:hidden flex flex-col gap-1.5 p-3 z-50 rounded-lg hover:bg-neuro-cyan/10 transition-all duration-300 ${
              isMenuOpen ? "relative" : ""
            }`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <span
              className={`w-7 h-0.5 bg-neuro-cyan shadow-sm shadow-neuro-cyan/50 transition-all duration-300 ${
                isMenuOpen ? "rotate-45 translate-y-2" : ""
              }`}
            ></span>
            <span
              className={`w-7 h-0.5 bg-neuro-cyan shadow-sm shadow-neuro-cyan/50 transition-all duration-300 ${
                isMenuOpen ? "opacity-0" : ""
              }`}
            ></span>
            <span
              className={`w-7 h-0.5 bg-neuro-cyan shadow-sm shadow-neuro-cyan/50 transition-all duration-300 ${
                isMenuOpen ? "-rotate-45 -translate-y-2" : ""
              }`}
            ></span>
          </button>

          <ul
            className={`${
              isMenuOpen
                ? "right-0 opacity-100"
                : "-right-full opacity-0 md:opacity-100"
            } fixed md:static top-[80px] md:top-0 flex-col md:flex-row md:flex gap-8 md:gap-10 items-center bg-dark-900/98 md:bg-transparent w-full md:w-auto p-10 md:p-0 shadow-2xl shadow-neuro-cyan/20 md:shadow-none transition-all duration-500 ease-in-out border-l border-neuro-cyan/30 md:border-none`}
          >
            {menuItems.map((item) => (
              <li
                key={item.id}
                className="w-full md:w-auto text-center md:text-left"
              >
                <a
                  onClick={() => scrollToSection(item.id)}
                  className="cursor-pointer font-semibold text-base md:text-sm text-gray-100 hover:text-neuro-cyan transition-all duration-300 relative group inline-block py-2 px-4 md:px-0 rounded-lg md:rounded-none hover:bg-neuro-cyan/10 md:hover:bg-transparent"
                >
                  {item.label}
                  <span className="absolute bottom-1 md:bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-neuro-cyan via-neuro-azure to-neuro-purple group-hover:w-[80%] md:group-hover:w-full transition-all duration-500 ease-out shadow-sm shadow-neuro-cyan"></span>
                </a>
              </li>
            ))}
            <li className="mt-4 md:mt-0">
              <LanguageSwitcher />
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;
